import React, { Fragment, useState, useEffect } from "react";
import { Tab, Tabs, Alert, Button, Form, Badge, Pagination, Table, Modal } from "react-bootstrap";
import StatusCardsManager from './StatusCardsManager';
import CardPreview from './CardPreview';
import PaymentManagement from './Payment/PaymentManagement';

// CSS temporairement désactivé pour éviter les erreurs d'import
// import './styles/GestionCartes.css';

// Styles personnalisés pour les sous-onglets comme dans l'ancienne version
const customTabStyles = `
  .nav-tabs-custom .nav-link {
    border-radius: 6px 6px 0 0;
    margin-right: 2px;
    font-weight: 500;
  }
  .nav-tabs-custom .nav-link.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: transparent;
    color: white;
  }
  .table thead th {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 0.5px;
  }
  .btn-group .btn {
    border-radius: 4px;
    margin: 0 1px;
  }
  .table tbody tr:hover {
    background-color: rgba(0,0,0,0.02);
  }
  .fw-medium {
    font-weight: 500;
  }
  .bg-opacity-10 {
    --bs-bg-opacity: 0.1;
  }
`;

// Injection des styles personnalisés dans le head
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('custom-tab-styles');
  if (!existingStyle) {
    const styleElement = document.createElement('style');
    styleElement.id = 'custom-tab-styles';
    styleElement.innerHTML = customTabStyles;
    document.head.appendChild(styleElement);
  }
}

// Style CSS inline pour forcer la visibilité
const forceVisibleStyles = {
  container: {
    color: '#2c3e50',
  },
  cardTitle: {
    color: '#2c3e50 !important',
    fontWeight: '600'
  },
  formLabel: {
    color: '#495057 !important',
    fontWeight: '500',
    marginBottom: '0.5rem'
  },
  formControl: {
    border: '1px solid #ced4da',
    color: '#2c3e50 !important',
    backgroundColor: '#fff !important'
  },
  resultCard: {
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
    backgroundColor: '#fff !important',
    color: '#2c3e50 !important'
  },
  cardNumber: {
    fontFamily: 'monospace',
    fontWeight: 'bold',
    fontSize: '1rem',
    color: '#2c3e50 !important',
    margin: '0'
  },
  cardId: {
    fontSize: '0.8rem',
    color: '#6c757d !important'
  },
  infoLabel: {
    color: '#495057 !important',
    fontWeight: '600',
    marginRight: '0.5rem'
  },
  infoValue: {
    color: '#2c3e50 !important',
    fontWeight: '500'
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#6c757d !important'
  }
};

// Fonction utilitaire pour obtenir le nom d'affichage du statut
const getStatusDisplay = (status) => {
  const statusMap = {
    'DataValidation': 'Validation données',
    'PhotoProcessing': 'Traitement photo',
    'TemplateApplication': 'Application template',
    'QualityControl': 'Contrôle qualité',
    'Printing': 'Impression',
    'Packaging': 'Emballage',
    'Shipped': 'Expédiée',
    'Delivered': 'Livrée',
    'Cancelled': 'Annulée',
    'Blocked': 'Bloquée'
  };
  return statusMap[status] || status;
};

const CardManagementContainer = () => {
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('cardNumber');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  
  const handleSearch = async (type, query) => {
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      
      if (type === 'cardId' && !isNaN(query)) {
        // Recherche directe par ID de carte
        const response = await fetch(`${baseUrl}/Card/${query}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const card = await response.json();
          const cardWithDisplay = {
            ...card,
            statusDisplay: getStatusDisplay(card.status)
          };
          setSearchResults([cardWithDisplay]);
        } else if (response.status === 404) {
          setSearchResults([]);
          setError(`Aucune carte trouvée avec l'ID ${query}`);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else if (type === 'cardNumber') {
        // Recherche par numéro de carte
        const response = await fetch(`${baseUrl}/Card/number/${query}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const card = await response.json();
          const cardWithDisplay = {
            ...card,
            statusDisplay: getStatusDisplay(card.status)
          };
          setSearchResults([cardWithDisplay]);
        } else if (response.status === 404) {
          setSearchResults([]);
          setError(`Aucune carte trouvée avec le numéro ${query}`);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else if (type === 'beneficiaryId') {
        // Recherche par ID bénéficiaire
        const response = await fetch(`${baseUrl}/Card/beneficiary/${query}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const cards = await response.json();
          const cardsWithDisplay = cards.map(card => ({
            ...card,
            statusDisplay: getStatusDisplay(card.status)
          }));
          setSearchResults(cardsWithDisplay);
        } else if (response.status === 404) {
          setSearchResults([]);
          setError(`Aucune carte trouvée pour le bénéficiaire ${query}`);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
      
    } catch (err) {
      console.error('Erreur de recherche:', err);
      setError(err.message);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div 
        className="gestion-cartes-container"
        style={forceVisibleStyles.container}
      >
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
          
          {/* Onglet Recherche */}
          <Tab eventKey="search" title={
            <span>
              <i className="la la-search me-2"></i>
              Recherche de Cartes
            </span>
          }>
            <div className="row">
              <div className="col-xl-6">
                <div className="card search-filters">
                  <div className="card-header">
                    <h5 className="card-title mb-0" style={forceVisibleStyles.cardTitle}>
                      <i className="la la-search me-2"></i>
                      Recherche de Cartes
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{color: '#495057', fontWeight: '500', marginBottom: '0.5rem'}}>Type de recherche</Form.Label>
                          <Form.Control
                            as="select"
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value)}
                            style={{border: '1px solid #ced4da', color: '#2c3e50'}}
                          >
                            <option value="cardNumber">Par numéro de carte</option>
                            <option value="cardId">Par ID de carte</option>
                            <option value="beneficiaryId">Par ID bénéficiaire</option>
                          </Form.Control>
                        </Form.Group>
                      </div>
                      
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label style={{color: '#495057', fontWeight: '500', marginBottom: '0.5rem'}}>Valeur de recherche</Form.Label>
                          <Form.Control
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={
                              searchType === 'cardNumber' ? 'Ex: AC2025123456789012' :
                              searchType === 'cardId' ? 'Ex: 789' :
                              'Ex: 12345'
                            }
                            disabled={loading}
                            style={{border: '1px solid #ced4da', color: '#2c3e50'}}
                          />
                        </Form.Group>
                      </div>
                    </div>
                    
                    <Button
                      variant="primary"
                      onClick={() => handleSearch(searchType, searchQuery)}
                      disabled={loading || !searchQuery.trim()}
                      className="w-100"
                    >
                      {loading ? (
                        <>
                          <i className="la la-spinner la-spin me-2"></i>
                          Recherche...
                        </>
                      ) : (
                        <>
                          <i className="la la-search me-2"></i>
                          Rechercher
                        </>
                      )}
                    </Button>

                    {error && (
                      <Alert variant="danger" className="mt-3">
                        <i className="la la-exclamation-triangle me-2"></i>
                        {error}
                      </Alert>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="col-xl-6">
                {/* Résultats */}
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title mb-0" style={{color: '#2c3e50', fontWeight: '600'}}>
                      <i className="la la-list me-2"></i>
                      Résultats
                    </h5>
                  </div>
                  <div className="card-body">
                    {loading && (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                        </div>
                        <div className="mt-3 text-muted">Chargement des cartes...</div>
                      </div>
                    )}
                    
                    {!loading && (!searchResults || searchResults.length === 0) && (
                      <div 
                        className="empty-state"
                        style={{
                          textAlign: 'center',
                          padding: '3rem 1rem',
                          color: '#6c757d'
                        }}
                      >
                        <i className="la la-search la-3x mb-3" style={{fontSize: '3rem', marginBottom: '1rem', opacity: '0.5', color: '#6c757d'}}></i>
                        <h6 style={{color: '#6c757d', marginBottom: '0.5rem'}}>Aucune carte trouvée</h6>
                        <p style={{color: '#6c757d', marginBottom: '0'}}>Effectuez une recherche pour voir les cartes.</p>
                      </div>
                    )}
                    
                    {searchResults && searchResults.length > 0 && (
                      <div>
                        {searchResults.map((card, index) => (
                          <div 
                            key={card.id || index} 
                            className="card-search-result"
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '8px',
                              padding: '1rem',
                              marginBottom: '1rem',
                              backgroundColor: '#fff',
                              color: '#2c3e50'
                            }}
                          >
                            <div className="card-header-info" style={{display: 'flex', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #eee'}}>
                              <i className="la la-id-card text-primary me-2 fs-5"></i>
                              <div>
                                <h6 
                                  className="card-number"
                                  style={{
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    color: '#2c3e50 !important',
                                    margin: '0'
                                  }}
                                >
                                  {card.cardNumber}
                                </h6>
                                <small 
                                  className="card-id"
                                  style={{
                                    fontSize: '0.8rem',
                                    color: '#6c757d'
                                  }}
                                >
                                  ID: {card.id}
                                </small>
                              </div>
                            </div>
                            
                            <div className="row align-items-center mt-3">
                              <div className="col-md-8">
                                <div className="card-info-row" style={{marginBottom: '0.5rem'}}>
                                  <span 
                                    className="card-info-label"
                                    style={{
                                      color: '#495057',
                                      fontWeight: '600',
                                      marginRight: '0.5rem'
                                    }}
                                  >
                                    Bénéficiaire:
                                  </span>
                                  <span 
                                    className="card-info-value beneficiary-name"
                                    style={{
                                      color: '#2c3e50',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {card.beneficiaryName}
                                  </span>
                                </div>
                                
                                <div className="card-info-row" style={{marginBottom: '0.5rem'}}>
                                  <span 
                                    className="card-info-label"
                                    style={{
                                      color: '#495057',
                                      fontWeight: '600',
                                      marginRight: '0.5rem'
                                    }}
                                  >
                                    Production:
                                  </span>
                                  <span 
                                    className="card-info-value"
                                    style={{
                                      color: '#2c3e50'
                                    }}
                                  >
                                    {new Date(card.productionDate).toLocaleDateString('fr-FR')}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="col-md-4 text-center">
                                <div className="mb-2">
                                  <strong style={{color: '#2c3e50'}}>Statut</strong>
                                </div>
                                <div className="mb-3">
                                  <span className="badge bg-success">{card.statusDisplay || card.status}</span>
                                </div>
                                <div>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedCard(card);
                                      setShowPreviewModal(true);
                                    }}
                                    className="me-2"
                                  >
                                    <i className="la la-eye me-1"></i>
                                    Prévisualiser
                                  </Button>
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                  >
                                    <i className="la la-cog me-1"></i>
                                    Actions
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Tab>

          {/* Onglet Cartes par Statut */}
          <Tab eventKey="status" title={
            <span>
              <i className="la la-list me-2"></i>
              Cartes par Statut
            </span>
          }>
            <StatusCardsManager />
          </Tab>

          {/* Onglet Paiements */}
          <Tab eventKey="payments" title={
            <span>
              <i className="fa fa-credit-card me-2"></i>
              Gestion des Paiements
            </span>
          }>
            <PaymentManagement />
          </Tab>

        </Tabs>
      </div>
      
      {/* Modal de prévisualisation de carte */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="la la-eye me-2"></i>
            Prévisualisation de la carte
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedCard && (
            <div>
              <div className="mb-3">
                <strong>Carte: </strong>{selectedCard.cardNumber}
                <br />
                <strong>Bénéficiaire: </strong>{selectedCard.beneficiaryName}
              </div>
              <CardPreview 
                cardData={selectedCard}
                size="large"
              />
              <div className="mt-3">
                <small className="text-muted">
                  Cette prévisualisation correspond au template qui sera utilisé pour l'impression de la carte.
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

// Composant pour les sous-onglets de cartes par statut avec le look and feel de l'ancienne version
const StatusCardsTabs = () => {
  const [activeTab, setActiveTab] = useState('delivered');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ cards: [], pagination: { totalItems: 0, totalPages: 0 } });
  const [statusCounts, setStatusCounts] = useState({
    delivered: 0,
    shipped: 0,
    printing: 0,
    qualityControl: 0,
    photoProcessing: 0,
    dataValidation: 0,
    blocked: 0,
    cancelled: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('productionDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const pageSize = 20;

  // Chargement des compteurs de statuts
  useEffect(() => {
    loadStatusCounts();
  }, []);

  // Chargement des cartes par statut quand les paramètres changent
  useEffect(() => {
    loadData();
  }, [activeTab, currentPage, searchTerm, sortField, sortOrder]);

  const loadStatusCounts = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const response = await fetch(`${baseUrl}/Card/status/counts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const counts = await response.json();
        setStatusCounts({
          delivered: counts.delivered || 0,
          shipped: counts.shipped || 0,
          printing: counts.printing || 0,
          qualityControl: counts.qualityControl || 0,
          photoProcessing: counts.photoProcessing || 0,
          dataValidation: counts.dataValidation || 0,
          blocked: counts.blocked || 0,
          cancelled: counts.cancelled || 0
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des compteurs:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const apiStatus = getStatusApiName(activeTab);
      
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize,
        status: apiStatus,
        sortBy: sortField,
        sortDirection: sortOrder
      });
      
      if (searchTerm) {
        params.append('searchTerm', searchTerm);
      }
      
      const response = await fetch(`${baseUrl}/Card/list?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData({
          cards: result.data || [],
          pagination: {
            totalItems: result.total || 0,
            totalPages: Math.ceil((result.total || 0) / pageSize)
          }
        });
      } else {
        setData({ cards: [], pagination: { totalItems: 0, totalPages: 0 } });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des cartes:', error);
      setData({ cards: [], pagination: { totalItems: 0, totalPages: 0 } });
    } finally {
      setLoading(false);
    }
  };

  const getStatusApiName = (tabKey) => {
    const mapping = {
      'dataValidation': 'DataValidation',
      'photoProcessing': 'PhotoProcessing',
      'qualityControl': 'QualityControl',
      'printing': 'Printing',
      'shipped': 'Shipped',
      'delivered': 'Delivered',
      'blocked': 'Blocked',
      'cancelled': 'Cancelled'
    };
    return mapping[tabKey] || 'Delivered';
  };

  const getStatusColor = (status) => {
    const colors = {
      'dataValidation': 'info',
      'photoProcessing': 'info',
      'qualityControl': 'warning',
      'printing': 'primary',
      'shipped': 'warning',
      'delivered': 'success',
      'blocked': 'danger',
      'cancelled': 'secondary'
    };
    return colors[status] || 'primary';
  };

  const onTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
    setSearchTerm('');
    setSortField('productionDate');
    setSortOrder('desc');
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const onSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const onSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'DataValidation': { variant: 'info', text: 'Validation données' },
      'PhotoProcessing': { variant: 'info', text: 'Traitement photo' },
      'TemplateApplication': { variant: 'info', text: 'Application template' },
      'QualityControl': { variant: 'warning', text: 'Contrôle qualité' },
      'Printing': { variant: 'primary', text: 'Impression' },
      'Packaging': { variant: 'primary', text: 'Emballage' },
      'Shipped': { variant: 'warning', text: 'Expédiée' },
      'Delivered': { variant: 'success', text: 'Livrée' },
      'Cancelled': { variant: 'danger', text: 'Annulée' },
      'Blocked': { variant: 'danger', text: 'Bloquée' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  // Pagination intelligente comme dans l'ancienne version
  const renderPagination = () => {
    if (data.pagination.totalPages <= 1) return null;
    
    const totalPages = data.pagination.totalPages;
    const current = currentPage;
    
    // Logique de pagination intelligente pour de gros volumes
    let startPage = Math.max(1, current - 2);
    let endPage = Math.min(totalPages, current + 2);
    
    if (current <= 3) {
      endPage = Math.min(5, totalPages);
    }
    if (current >= totalPages - 2) {
      startPage = Math.max(1, totalPages - 4);
    }
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Affichage {((current - 1) * pageSize) + 1} à {Math.min(current * pageSize, data.pagination.totalItems)} sur {data.pagination.totalItems.toLocaleString()} cartes
        </div>
        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(1)} disabled={current === 1}>
                <i className="la la-angle-double-left"></i>
              </button>
            </li>
            <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(current - 1)} disabled={current === 1}>
                <i className="la la-angle-left"></i>
              </button>
            </li>
            
            {startPage > 1 && (
              <>
                <li className="page-item">
                  <button className="page-link" onClick={() => onPageChange(1)}>1</button>
                </li>
                {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
              </>
            )}
            
            {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
              <li key={page} className={`page-item ${page === current ? 'active' : ''}`}>
                <button className="page-link" onClick={() => onPageChange(page)}>{page}</button>
              </li>
            ))}
            
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                <li className="page-item">
                  <button className="page-link" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
                </li>
              </>
            )}
            
            <li className={`page-item ${current === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(current + 1)} disabled={current === totalPages}>
                <i className="la la-angle-right"></i>
              </button>
            </li>
            <li className={`page-item ${current === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(totalPages)} disabled={current === totalPages}>
                <i className="la la-angle-double-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  return (
    <div>
      {/* Sous-onglets avec compteurs */}
      <div className="nav-tabs-custom mb-4">
        <Tabs activeKey={activeTab} onSelect={onTabChange}>
          <Tab eventKey="delivered" title={`Livrées (${statusCounts.delivered.toLocaleString()})`} />
          <Tab eventKey="shipped" title={`Expédiées (${statusCounts.shipped.toLocaleString()})`} />
          <Tab eventKey="printing" title={`Impression (${statusCounts.printing.toLocaleString()})`} />
          <Tab eventKey="qualityControl" title={`Contrôle qualité (${statusCounts.qualityControl.toLocaleString()})`} />
          <Tab eventKey="photoProcessing" title={`Photo (${statusCounts.photoProcessing.toLocaleString()})`} />
          <Tab eventKey="dataValidation" title={`Validation (${statusCounts.dataValidation.toLocaleString()})`} />
          <Tab eventKey="blocked" title={`Bloquées (${statusCounts.blocked.toLocaleString()})`} />
          <Tab eventKey="cancelled" title={`Annulées (${statusCounts.cancelled.toLocaleString()})`} />
        </Tabs>
      </div>
      
      {/* Barre d'outils compacte */}
      <div className="bg-light border rounded p-3 mb-3">
        <div className="row align-items-center g-2">
          <div className="col-md-4">
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white border-end-0">
                <i className="la la-search text-muted"></i>
              </span>
              <Form.Control
                type="text"
                placeholder="Rechercher par numéro ou nom..."
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
                className="border-start-0"
              />
            </div>
          </div>
          <div className="col-md-3">
            <Form.Control
              as="select"
              size="sm"
              value={`${sortField}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                onSort(field, order);
              }}
            >
              <option value="productionDate-desc">Plus récentes d'abord</option>
              <option value="productionDate-asc">Plus anciennes d'abord</option>
              <option value="beneficiaryName-asc">Nom A-Z</option>
              <option value="beneficiaryName-desc">Nom Z-A</option>
              <option value="cardNumber-asc">Numéro croissant</option>
              <option value="cardNumber-desc">Numéro décroissant</option>
            </Form.Control>
          </div>
          <div className="col-md-5 text-md-end">
            <div className="d-flex align-items-center justify-content-md-end gap-3">
              <Badge bg={getStatusColor(activeTab)} className="px-3 py-2">
                <i className="la la-id-card me-1"></i>
                {data.pagination.totalItems.toLocaleString()} cartes
              </Badge>
              <small className="text-muted fw-medium">
                <i className="la la-layer-group me-1"></i>
                Page {currentPage} / {data.pagination.totalPages}
              </small>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tableau des cartes */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
              </div>
              <div className="mt-3 text-muted">Chargement des cartes...</div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className={`table-${getStatusColor(activeTab)}`}>
                    <tr>
                      <th className="border-0">Numéro de Carte</th>
                      <th className="border-0">Bénéficiaire</th>
                      <th className="border-0">Date Production</th>
                      <th className="border-0">Statut</th>
                      <th className="border-0">Informations Spécifiques</th>
                      <th className="border-0 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cards.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <i className="la la-search la-2x text-muted mb-2 d-block"></i>
                          <h6 className="text-muted">Aucune carte trouvée</h6>
                          <small className="text-muted">Essayez de modifier vos critères de recherche</small>
                        </td>
                      </tr>
                    ) : (
                      data.cards.map((card, index) => (
                        <tr key={card.cardId || card.id} className="align-middle">
                          <td>
                            <div className="d-flex align-items-center">
                              <div style={{width: '3px', height: '40px'}}></div>
                              <div>
                                <div className="fw-bold">{card.cardNumber}</div>
                                <small className="text-muted">ID: {card.cardId || card.id}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="fw-medium">{card.beneficiaryName || `Bénéficiaire ${card.beneficiaryId}`}</div>
                          </td>
                          <td>
                            <div>{card.productionDate ? new Date(card.productionDate).toLocaleDateString('fr-FR') : 'N/A'}</div>
                            {card.productionDate && (
                              <small className="text-muted">
                                {new Date(card.productionDate).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                              </small>
                            )}
                          </td>
                          <td>
                            {getStatusBadge(getStatusApiName(activeTab))}
                          </td>
                          <td>
                            {/* Informations spécifiques selon le statut */}
                            {card.blockReason && (
                              <div className="text-danger">
                                <div className="fw-medium">
                                  <i className="la la-exclamation-triangle me-1"></i>
                                  {card.blockReason}
                                </div>
                                <small className="text-muted">
                                  Bloquée le {card.blockedDate ? new Date(card.blockedDate).toLocaleDateString('fr-FR') : 'N/A'} par {card.blockedBy || 'Système'}
                                </small>
                              </div>
                            )}
                            
                            {card.trackingNumber && (
                              <div className="text-info">
                                <div className="fw-medium">
                                  <i className="la la-shipping-fast me-1"></i>
                                  {card.trackingNumber}
                                </div>
                                <small className="text-muted">
                                  Expédiée le {card.shippedDate ? new Date(card.shippedDate).toLocaleDateString('fr-FR') : 'N/A'} via {card.carrier || 'Transporteur'}
                                </small>
                              </div>
                            )}
                            
                            {card.deliveredDate && (
                              <div className="text-success">
                                <div className="fw-medium">
                                  <i className="la la-check-circle me-1"></i>
                                  Livrée le {new Date(card.deliveredDate).toLocaleDateString('fr-FR')}
                                </div>
                              </div>
                            )}
                            
                            {!card.blockReason && !card.trackingNumber && !card.deliveredDate && (
                              <small className="text-muted">En cours de traitement</small>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="btn-group" role="group">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  setSelectedCard(card);
                                  setShowPreviewModal(true);
                                }}
                                title="Prévisualiser"
                              >
                                <i className="la la-eye"></i>
                              </Button>
                              
                              {activeTab === 'blocked' ? (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  title="Débloquer"
                                >
                                  <i className="la la-unlock"></i>
                                </Button>
                              ) : (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  title="Bloquer"
                                >
                                  <i className="la la-ban"></i>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              
              {/* Footer avec pagination */}
              {data.pagination.totalPages > 1 && (
                <div className="card-footer bg-light">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardManagementContainer;